'use client'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu'
import { useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { ArrowLeftRightIcon, EllipsisIcon, ShareIcon } from 'lucide-react'
import Link from 'next/link'
import { useState } from 'react'
import { useSelectedTickerInfo } from '@/src/hooks/token-info/use-selected-ticker'
import { useTokenActions } from '@/src/hooks/token-info/use-token-actions'
import { assetDataQuery } from '@/src/queries/keys/token-info/asset-data'
import { assetMarketChartQuery } from '@/src/queries/keys/token-info/asset-market-chart'
import { priceChartTimeIntervalAtom } from '@/src/stores/token-pages/atoms'
import { useShareButton } from '../../insight-article/share-button'
import { Button } from '../../ui/button'
import Chart, { type ChartHoverData } from '.'
import ChartHeader from './header'

export default function ChartSection() {
  const [chartHoverData, setChartHoverData] = useState<ChartHoverData>(null)
  const { primaryToken, secondaryToken, chain, denom, isLoading, ticker } =
    useSelectedTickerInfo()
  const { getTokenInfoUrl } = useTokenActions()
  const selectedTimeInterval = useAtomValue(priceChartTimeIntervalAtom)
  const { handleShare } = useShareButton()

  const {
    data: primaryTokenInfoAssetData,
    isLoading: isPrimaryAssetDataLoading,
  } = useQuery({
    ...assetDataQuery({
      chainId: primaryToken?.chain_id ?? chain,
      denom: primaryToken?.denom ?? denom,
    }),
    enabled: !!chain && !!denom,
    staleTime: 2 * 60 * 1000,
  })

  const {
    data: secondaryTokenInfoAssetData,
    isLoading: isSecondaryAssetDataLoading,
  } = useQuery({
    ...assetDataQuery({
      chainId: secondaryToken?.chain_id!,
      denom: secondaryToken?.denom!,
    }),
    enabled: !!secondaryToken?.chain_id && !!secondaryToken.denom,
    staleTime: 2 * 60 * 1000,
  })

  const { data: chartData, isLoading: isLoadingChartData } = useQuery({
    ...assetMarketChartQuery({
      chainId: chain,
      denom,
      range: selectedTimeInterval?.value,
    }),
    enabled: !!chain && !!denom && !!selectedTimeInterval,
    staleTime: 2 * 60 * 1000,
  })

  const { data: secondaryChartData, isLoading: isLoadingSecondaryChartData } =
    useQuery({
      ...assetMarketChartQuery({
        chainId: secondaryToken?.chain_id ?? chain,
        denom: secondaryToken?.denom ?? denom,
        range: selectedTimeInterval?.value,
      }),
      enabled: secondaryToken && !!selectedTimeInterval,
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
    })

  const url =
    secondaryToken?.chain_id && secondaryToken?.denom && secondaryToken?.ticker
      ? getTokenInfoUrl({
          chain_id: secondaryToken.chain_id,
          denom: secondaryToken.denom,
          ticker: secondaryToken.ticker,
          direction: 'context-switch',
        })
      : undefined

  const isSameKindOfToken = secondaryToken?.kind === primaryToken?.kind

  return (
    <>
      <div className='group/chart-section @container/chart-section flex @lg:grid grid-cols-2 items-start justify-between gap-2 @lg:gap-8 pt-3 -mx-4'>
        <ChartHeader
          className='px-4 py-3 group-has-[.second-token-chart-header:hover]/chart-section:opacity-30 transition-all duration-300'
          chartHoverData={chartHoverData}
          tokenData={primaryToken}
          tokenInfoAssetData={primaryTokenInfoAssetData}
          isLoading={isLoading}
          isAssetDataLoading={isPrimaryAssetDataLoading}
          isChartDataLoading={isLoadingChartData}
          priceChange={
            chartData?.priceChange ??
            primaryTokenInfoAssetData?.market_data
              .price_change_percentage_24h ??
            0
          }
          ticker={primaryToken?.ticker ?? ticker ?? ''}
        />
        {secondaryToken !== undefined &&
          !isSameKindOfToken &&
          url !== undefined && (
            <Link
              prefetch
              href={url}
              scroll={false}
              className='group px-4 py-3 second-token-chart-header cursor-pointer hidden md:block opacity-30 border-button-outline-border border-1 rounded-full h-12 @lg:h-auto mr-4 @lg:mr-0 @lg:border-0 @lg:rounded-2xl mt-1.5 @lg:mt-0 hover:opacity-100 hover:bg-surface1 transition-all duration-200'
            >
              <ChartHeader
                tokenData={secondaryToken}
                tokenInfoAssetData={secondaryTokenInfoAssetData}
                isLoading={isLoading}
                isAssetDataLoading={isSecondaryAssetDataLoading}
                isChartDataLoading={isLoadingSecondaryChartData}
                priceChange={
                  secondaryChartData?.priceChange ??
                  secondaryTokenInfoAssetData?.market_data
                    .price_change_percentage_24h ??
                  0
                }
                cta={
                  <p className='flex items-center gap-1 text-link'>
                    <ArrowLeftRightIcon size={16} /> View token details
                  </p>
                }
                ticker={secondaryToken?.ticker ?? ''}
                variant='compact-on-mobile'
              />
            </Link>
          )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              size='sm'
              className='p-1.5 md:hidden ml-auto'
            >
              <EllipsisIcon className='size-5' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuPortal>
            <DropdownMenuContent className='flex flex-col w-[240px] max-h-72 relative overflow-auto no-scrollbar p-2 mt-2 mr-2 data-[state=open]:animate-enter-scale data-[side=top]:origin-bottom data-[side=bottom]:origin-top data-[state=closed]:animate-exit-scale bg-foreground border border-border1 rounded-2xl'>
              <DropdownMenuItem
                onClick={() => handleShare(true)}
                className='flex gap-3 items-center h-10 px-3  rounded-xl outline-none cursor-pointer'
              >
                <ShareIcon className='size-5 text-tertiary-100' />

                <p className='text-primary font-semibold text-base leading-6'>
                  Share page
                </p>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenu>
      </div>
      <Chart
        chartData={chartData}
        ticker={ticker ?? ''}
        isLoadingChartData={isLoadingChartData}
        focusedPoint={chartHoverData}
        onChartHover={setChartHoverData}
      />
    </>
  )
}
